import React, { useState, useEffect } from 'react';
import { 
  Settings, User, Lock, ShieldCheck, Mail, Phone, 
  Globe, KeyRound, Check, AlertCircle, Crown, UserCheck, Building2, Users
} from 'lucide-react';
import { managerAPI } from '../utils/api';

const ManagerSettings = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [passwordData, setPasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passLoading, setPassLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await managerAPI.getProfile();
        if (res.data?.success) {
          setProfile(res.data.manager);
        }
      } catch (err) {
        console.error("Failed to load profile:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match!' });
      return;
    }

    setPassLoading(true);
    try {
      const res = await managerAPI.changePassword({
        oldPassword: passwordData.oldPassword,
        newPassword: passwordData.newPassword
      });
      setMessage({ type: 'success', text: res.data?.message || 'Password successfully updated!' });
      setPasswordData({ oldPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'Failed to update password.' });
    } finally {
      setPassLoading(false);
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500 max-w-4xl mx-auto">
        {/* Header */}
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-white tracking-tight flex items-center gap-3">
            <Settings className="text-indigo-400" size={28} />
            <span>Executive Account Settings</span>
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            Manage your Manager credentials, security preferences, and view organizational hierarchy.
          </p>
        </div>

        {/* Profile Card */}
        <div className="bg-[#0b0f19]/80 border border-indigo-500/15 rounded-[32px] p-6 md:p-8 backdrop-blur-xl">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 pb-6 border-b border-white/5">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-tr from-indigo-600 via-purple-600 to-violet-500 flex items-center justify-center text-white font-bold text-2xl shadow-lg shadow-indigo-600/30">
                <Crown size={30} />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">
                  {profile?.first_name} {profile?.last_name || ''}
                </h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className="px-2.5 py-0.5 rounded-full bg-indigo-500/15 text-indigo-300 text-xs font-mono font-bold border border-indigo-500/20">
                    {profile?.user_id || profile?.xoxo_id || '10001'}
                  </span>
                  <span className="text-xs text-slate-400 font-medium">Executive Manager</span>
                </div>
              </div>
            </div>

            <span className="px-3 py-1.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 font-bold text-xs w-fit">
              Active Executive Account
            </span>
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-6">
            <div>
              <p className="text-xs text-slate-400 font-bold uppercase tracking-wider mb-1">Email Address</p>
              <p className="text-white text-sm font-semibold">{profile?.email || 'N/A'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 font-bold uppercase tracking-wider mb-1">Phone Number</p>
              <p className="text-white text-sm font-semibold">{profile?.phone || 'N/A'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 font-bold uppercase tracking-wider mb-1">Operating Country</p>
              <p className="text-white text-sm font-semibold">{profile?.country || 'India'}</p>
            </div>
          </div>
        </div>

        {/* Hierarchy Overview Card */}
        <div className="bg-[#0b0f19]/80 border border-indigo-500/15 rounded-[32px] p-6 md:p-8 backdrop-blur-xl">
          <h3 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
            <Crown size={20} className="text-indigo-400" />
            <span>Organizational Hierarchy Standing</span>
          </h3>
          <p className="text-xs text-slate-400 mb-6">
            Your team scale and managerial downstream reach.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/5 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400 font-bold">
                <UserCheck size={20} />
              </div>
              <div>
                <p className="text-xs text-slate-400">Direct BDs</p>
                <p className="text-lg font-bold text-white">{profile?.bds_count || 0}</p>
              </div>
            </div>

            <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/5 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400 font-bold">
                <Building2 size={20} />
              </div>
              <div>
                <p className="text-xs text-slate-400">Managed Agencies</p>
                <p className="text-lg font-bold text-white">{profile?.agencies_count || 0}</p>
              </div>
            </div>

            <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/5 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-400 font-bold">
                <Users size={20} />
              </div>
              <div>
                <p className="text-xs text-slate-400">Network Hosts</p>
                <p className="text-lg font-bold text-white">{profile?.hosts_count || 0}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Change Password Card */}
        <div className="bg-[#0b0f19]/80 border border-indigo-500/15 rounded-[32px] p-6 md:p-8 backdrop-blur-xl">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400">
              <Lock size={20} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Security & Password</h3>
              <p className="text-xs text-slate-400">Update your Manager portal access password</p>
            </div>
          </div>

          {message.text && (
            <div className={`p-4 rounded-2xl text-xs font-semibold mb-6 flex items-center gap-2 ${
              message.type === 'success' 
                ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400' 
                : 'bg-red-500/10 border border-red-500/20 text-red-400'
            }`}>
              {message.type === 'success' ? <Check size={16} /> : <AlertCircle size={16} />}
              <span>{message.text}</span>
            </div>
          )}

          <form onSubmit={handlePasswordSubmit} className="space-y-4 max-w-lg">
            <div>
              <label className="text-xs font-bold text-slate-400 block mb-2">Current Password</label>
              <input
                type="password"
                required
                value={passwordData.oldPassword}
                onChange={(e) => setPasswordData({ ...passwordData, oldPassword: e.target.value })}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-600 outline-none focus:border-indigo-500 text-sm"
                placeholder="Enter current password"
              />
            </div>

            <div>
              <label className="text-xs font-bold text-slate-400 block mb-2">New Password</label>
              <input
                type="password"
                required
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-600 outline-none focus:border-indigo-500 text-sm"
                placeholder="At least 6 characters"
              />
            </div>

            <div>
              <label className="text-xs font-bold text-slate-400 block mb-2">Confirm New Password</label>
              <input
                type="password"
                required
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-600 outline-none focus:border-indigo-500 text-sm"
                placeholder="Re-enter new password"
              />
            </div>

            <button
              type="submit"
              disabled={passLoading}
              className="px-6 py-3 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-bold text-xs shadow-lg shadow-indigo-600/30 transition-all disabled:opacity-50 cursor-pointer"
            >
              {passLoading ? 'Updating...' : 'Save New Password'}
            </button>
          </form>
        </div>
      </div>
  );
};

export default ManagerSettings;
